import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useReducer,
  useRef,
  useState,
} from 'react';
import invariant from 'tiny-invariant';
import { usePossibleSortableItemContext } from './SortableItemContext';

export type DraggableState =
  | { type: 'irrelevant' }
  | { type: 'idle' }
  | {
      type: 'preview';
      id: number;
      container: HTMLElement;
      dragged: HTMLElement | null;
    }
  | { type: 'dragging'; id: number };
const idleState: DraggableState = { type: 'idle' };
const irrelevantState: DraggableState = { type: 'irrelevant' };

type SortableContextValue = {
  instanceId: symbol;
  draggableState: DraggableState;
  setDraggableState: Dispatch<SetStateAction<DraggableState>>;
  hoverId: number | null;
  setHoverId: Dispatch<SetStateAction<number | null>>;
  targets: number[];
  setTargets: Dispatch<SetStateAction<number[]>>;
  overItem: (id: number) => boolean;
  overThisItem: (id: number) => boolean;
  overList: (id: number) => boolean;
  overThisList: (id: number) => boolean;
};

const SortableContext = createContext<SortableContextValue | null>(null);

export function useSortableContext() {
  const context = useContext(SortableContext);
  invariant(context !== null);
  return context;
}

export function usePossibleSortableContext() {
  try {
    return useContext(SortableContext);
  } catch {
    return null;
  }
}

type SortableContextProviderProps = {};

export const SortableContextProvider = (
  props: SortableContextProviderProps & PropsWithChildren,
) => {
  const { children } = props;
  const [instanceId] = useState(() => Symbol('SortableContext'));
  const [draggableState, setDraggableState] =
    useState<DraggableState>(idleState);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const targetsRef = useRef<number[]>([]);
  // Dummy state to force re-render when targets change
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // setTargets updates the ref and triggers a re-render
  const setTargets: Dispatch<SetStateAction<number[]>> = (value) => {
    if (typeof value === 'function') {
      // @ts-ignore
      targetsRef.current = value(targetsRef.current);
    } else {
      targetsRef.current = value;
    }
    forceUpdate();
  };

  function overItem(id: number): boolean {
    if (targetsRef.current.length <= 1) return false;
    return targetsRef.current.some((v, i) => {
      return i < targetsRef.current.length - 1 && v === id;
    });
  }

  function overThisItem(id: number): boolean {
    if (targetsRef.current.length <= 1) return false;
    return targetsRef.current[0] === id;
  }

  function overList(id: number) {
    return targetsRef.current[targetsRef.current.length - 1] === id;
  }

  function overThisList(id: number) {
    return targetsRef.current.length === 1 && overList(id);
  }

  // Memoize only the values that benefit from memoization
  const contextValue = {
    instanceId,
    draggableState,
    setDraggableState,
    hoverId,
    setHoverId,
    targets: targetsRef.current,
    setTargets,
    overItem,
    overThisItem,
    overList,
    overThisList,
  };

  return (
    <SortableContext.Provider value={contextValue}>
      {children}
    </SortableContext.Provider>
  );
};

export function useDraggingState() {
  const sortableContext = usePossibleSortableContext();
  const itemContext = usePossibleSortableItemContext();
  if (sortableContext === null || itemContext === null)
    return {
      isIdle: false,
      isDragging: false,
      isPreview: false,
      state: irrelevantState,
      container: undefined,
      dragged: undefined,
      id: undefined,
    };
  const { draggableState } = sortableContext;
  if (draggableState.type === 'idle') {
    return {
      isIdle: true,
      isDragging: false,
      isPreview: false,
      state: 'idle',
      container: undefined,
      dragged: undefined,
      id: undefined,
    };
  }
  if (
    (draggableState.type === 'dragging' || draggableState.type === 'preview') &&
    draggableState.id === itemContext.id
  ) {
    return {
      isIdle: false,
      isDragging: draggableState.type === 'dragging',
      isPreview: draggableState.type === 'preview',
      state: draggableState.type,
      // Optionally, add container/dragged if needed
      ...(draggableState.type === 'preview'
        ? {
            container: draggableState.container,
            dragged: draggableState.dragged,
            id: draggableState.id,
          }
        : draggableState.type === 'dragging'
          ? {
              container: undefined,
              dragged: undefined,
              id: draggableState.id,
            }
          : { container: undefined, dragged: undefined, id: undefined }),
    };
  }
  return {
    isIdle: false,
    isDragging: false,
    isPreview: false,
    state: irrelevantState,
    container: undefined,
    dragged: undefined,
    id: undefined,
  };
}

/* Context specifically for the preview component */
export const SortablePreviewContext = createContext(false);
export function useSortablePreview() {
  return useContext(SortablePreviewContext);
}
